import type { ReactNode } from 'react';

import { ReputationConnectionSetupBanner } from '@/core/components/reputation/reputation-connection-setup-banner';
import { ReputationSyncBanner } from '@/core/components/reputation/reputation-sync-banner';
import {
  ReputationSyncProvider,
  useReputationSync,
} from '@/core/components/reputation/reputation-sync-context';
import { useInvalidateOnIonViewEnter } from '@/core/hooks/use-invalidate-on-ion-view-enter';
import { useReputationConnectionSetupState } from '@/core/hooks/use-reputation-connection-setup';
import { reputationQueryKeys } from '@/core/query-keys/reputation-query-keys';

type ReputationPageBannersProps = {
  /** Hide connection setup CTA when the page already covers it (e.g. setup → Connections tab). */
  hideConnectionBanner?: boolean;
};

export function ReputationPageBanners({
  hideConnectionBanner = false,
}: ReputationPageBannersProps) {
  const { banner } = useReputationSync();
  const setup = useReputationConnectionSetupState();

  const showSyncBanner = banner.visible && !setup.suppressSyncBanner;
  const showConnectionBanner =
    !hideConnectionBanner &&
    setup.showConnectionBanner &&
    setup.bannerVariant != null;

  if (!showConnectionBanner && !showSyncBanner) {
    return null;
  }

  return (
    <div className="flex flex-col gap-3">
      {showConnectionBanner && setup.bannerVariant ? (
        <ReputationConnectionSetupBanner
          variant={setup.bannerVariant}
          needsActionCount={setup.needsActionCount}
        />
      ) : null}
      {showSyncBanner ? <ReputationSyncBanner /> : null}
    </div>
  );
}

type ReputationLayoutProps = {
  children: ReactNode;
};

/** Sync context + vertical spacing for reputation pages. Place banners via {@link ReputationPageBanners}. */
export function ReputationLayout({ children }: ReputationLayoutProps) {
  useInvalidateOnIonViewEnter([reputationQueryKeys.root, ['reviews']]);

  return (
    <ReputationSyncProvider>
      <div className="flex min-w-0 flex-col gap-[var(--app-section-gap)]">
        {children}
      </div>
    </ReputationSyncProvider>
  );
}
